import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { apiRequest } from "../../../services/apiService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SurveyListHeader from "../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../component/common/layout/SurveyListSidebar";
import AccountSettingSidebar from "../../../component/common/layout/AccountSettingSidebar";
import NestedMenuToggle from "./NestedMenuToggle";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";

const editorExtensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3, 4, 5, 6] },
    bulletList: true,
    orderedList: true,
  }),
  Underline,
  LinkExtension.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
  TextStyle,
  Color.configure({ types: ["textStyle"] }),
  Table.configure({ resizable: true }),
  TableRow,
  TableHeader,
  TableCell,
];

function EditorToolbar({ editor, colorInputRef, currentColor, setCurrentColor, t }) {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt(t('settings.editor.url_prompt'), previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor, t]);

  return (
    <div className="d-flex flex-wrap gap-2 mb-3">
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleBold().run()} disabled={!editor}>{t('settings.editor.bold')}</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={!editor}>{t('settings.editor.italic')}</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleUnderline().run()} disabled={!editor}>{t('settings.editor.underline')}</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} disabled={!editor}>H1</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} disabled={!editor}>H2</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} disabled={!editor}>H3</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleBulletList().run()} disabled={!editor}>{t('settings.editor.bulleted_list')}</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().toggleOrderedList().run()} disabled={!editor}>{t('settings.editor.numbered_list')}</button>
      <button className="btn btn-bordered" onClick={setLink} disabled={!editor}>{t('settings.editor.link')}</button>
      <button className="btn btn-bordered" onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} disabled={!editor}>{t('settings.editor.table')}</button>
      <div style={{ position: "relative" }}>
        <button className="btn btn-bordered" disabled={!editor} onClick={() => colorInputRef.current?.click()} title={t('settings.editor.text_color')}>
          <span style={{ borderBottom: `3px solid ${currentColor}`, paddingBottom: 1 }}>A</span> {t('settings.editor.color')}
        </button>
        <input
          ref={colorInputRef}
          type="color"
          value={currentColor}
          style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
          onChange={(e) => {
            const color = e.target.value;
            setCurrentColor(color);
            editor?.chain().focus().setColor(color).run();
          }}
        />
      </div>
      <button className="btn btn-bordered" disabled={!editor} onClick={() => { editor?.chain().focus().unsetColor().run(); setCurrentColor("#000000"); }} title={t('settings.editor.no_color')}>{t('settings.editor.no_color')}</button>
    </div>
  );
}

const PAGE_CONFIG = {
  "privacy-policy": { field: "privacy_policy", i18nKey: "settings.organization_details.privacy_policy" },
  "terms-of-use": { field: "terms_of_use", i18nKey: "settings.organization_details.terms_of_use" },
  "cookie-policy": { field: "cookie_policy", i18nKey: "settings.organization_details.cookie_policy" },
};

const OrganizationDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathParts = location.pathname.split("/");
  const activeTab = pathParts[pathParts.length - 1] || "privacy-policy";
  const config = PAGE_CONFIG[activeTab] || PAGE_CONFIG["privacy-policy"];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgData, setOrgData] = useState(null);

  const colorRef = useRef(null);
  const [currentColor, setCurrentColor] = useState("#000000");

  const editor = useEditor({
    extensions: editorExtensions,
    content: "",
    autofocus: false,
    editable: false,
  });

  const justSavedRef = useRef(false);
  const loadedTabRef = useRef(null);

  useEffect(() => {
    if (!editor) return;
    const check = () => {
      const canEdit = window.modulePermissions?.M8?.edit == 1;
      if (editor.isEditable !== canEdit) {
        editor.setEditable(canEdit);
      }
    };
    check();
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, [editor]);

  const fetchData = async () => {
    try {
      const { response } = await apiRequest({ method: "GET", url: "/organization-details" });
      if (response?.data?.data) {
        setOrgData(response.data.data);
      }
    } catch (e) {
      console.error("Error loading organization details:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!editor) return;
    if (justSavedRef.current) {
      justSavedRef.current = false;
      return;
    }
    if (orgData) {
      const content = orgData[config.field] || "";
      editor.commands.setContent(content);
    } else {
      editor.commands.setContent("");
    }
    loadedTabRef.current = activeTab;
  }, [editor, orgData, activeTab]);

  const pageTitle = t(config.i18nKey);

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      const payload = {
        privacy_policy: orgData?.privacy_policy || "",
        terms_of_use: orgData?.terms_of_use || "",
        cookie_policy: orgData?.cookie_policy || "",
      };
      payload[config.field] = editor.getHTML();

      const { response, error } = await apiRequest({
        method: "PUT",
        url: "/organization-details",
        data: payload,
        showErrorToast: false,
      });
      if (response?.data?.statusCode === 200) {
        toast.success(t('settings.organization_details_page.success', { title: pageTitle }));
        justSavedRef.current = true;
        setOrgData({ ...orgData, [config.field]: editor.getHTML() });
      } else {
        toast.error(error?.response?.data?.msg || t('settings.organization_details_page.error'));
      }
    } catch (e) {
      toast.error(t('settings.organization_details_page.error'));
    }
    setSaving(false);
  };

  return (
    <>
      <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
        <SurveyListHeader />
        <div id="viewport" className="d-flex bg-white">
          <SurveyListSidebar />
          <div className="flex-grow-1 d-flex">
            <AccountSettingSidebar />
            <div className="flex-grow-1 page-content-wrap">
              <div className="page-heading">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/profile">{t('settings.title')}</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/admin/settings/organization-details/privacy-policy">{t('settings.organization_details.breadcrumb_heading')}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      <strong>{pageTitle}</strong>
                    </li>
                  </ol>
                </nav>
                <div className="d-flex align-items-center">
                  <NestedMenuToggle />
                  <div className="flex-grow-1 ps-3 ps-lg-0">
                    <h1>{pageTitle}</h1>
                    <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                      {t('settings.organization_details_page.edit_description', { title: pageTitle.toLowerCase() })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="profile-wrapper">
                {loading ? (
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                    <span className="ms-2">{t('settings.loading')}</span>
                  </div>
                ) : (
                  <>
                    <div className="card mb-4">
                      <div className="card-body">
                        <EditorToolbar editor={editor} colorInputRef={colorRef} currentColor={currentColor} setCurrentColor={setCurrentColor} t={t} />
                        <div className="editor-wrap privacy-editor" style={{ border: "1px solid #dee2e6", borderRadius: "8px", padding: "12px", minHeight: "300px" }}>
                          <EditorContent editor={editor} />
                        </div>
                        <style>{`
                          .privacy-editor .ProseMirror { min-height: 280px; outline: none; }
                          .privacy-editor .ProseMirror table { width: 100%; border-collapse: collapse; }
                          .privacy-editor .ProseMirror th, .privacy-editor .ProseMirror td { border: 1px solid #000; padding: 8px; }
                          .privacy-editor .ProseMirror thead th { background: #f2f2f2; font-weight: 600; }
                        `}</style>
                      </div>
                    </div>

                    {window.modulePermissions?.M8?.edit == 1 && (
                    <div className="btn-wrap">
                      <button
                        className="formsbutton btn-bordered btn"
                        onClick={handleSave}
                        disabled={saving || !editor}
                      >
                        {saving ? (
                          <>
                            <span className="white-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span className="visually-hidden">{t('settings.loading')}</span>
                          </>
                        ) : (
                          t('settings.save_changes')
                        )}
                      </button>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganizationDetails;
